import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Flex, Text, IconButton, VStack, HStack, Badge, Spinner,
  useDisclosure, AlertDialog, AlertDialogOverlay, AlertDialogContent,
  AlertDialogHeader, AlertDialogBody, AlertDialogFooter, Button,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { useMICUTheme } from '../lib/micu/MICUThemeProvider';
import { db, deleteCase } from '../lib/micu/micuDatabase';
import { getPendingSyncCount } from '../lib/micu/syncEngine';

const SyncBadge = ({ status, pendingCount }) => {
  if (status === 'synced' && pendingCount === 0) {
    return <Badge colorScheme="green" fontSize="2xs" variant="subtle">Synced</Badge>;
  }
  if (!navigator.onLine) {
    return <Badge colorScheme="gray" fontSize="2xs" variant="subtle">Offline</Badge>;
  }
  return <Badge colorScheme="yellow" fontSize="2xs" variant="subtle">Pending</Badge>;
};

const CaseRow = ({ c, patients, deviceCounts, medCounts, onDelete, navigate, isDark }) => {
  const patient = patients[c.id];
  const deviceCount = deviceCounts[c.id] || 0;
  const medCount = medCounts[c.id] || 0;
  const date = new Date(c.createdAt);
  const formatted = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;

  return (
    <Flex
      p={4}
      bg={isDark ? 'micu.card' : 'micu.card'}
      border="1px solid"
      borderColor="micu.border"
      borderRadius="lg"
      cursor="pointer"
      _hover={{ bg: isDark ? 'micu.cardHover' : 'micu.cardHover' }}
      onClick={() => navigate(`/micu-mobile/case/${c.id}`)}
      justify="space-between"
      align="center"
    >
      <Box flex={1}>
        <Text fontWeight="600" fontSize="md" color="micu.text">{c.name}</Text>
        <HStack spacing={3} mt={1}>
          <Text fontSize="xs" color="micu.textMuted">{formatted}</Text>
          {patient && (
            <Text fontSize="xs" color="micu.textSecondary">
              {patient.gender === 'M' ? 'Male' : patient.gender === 'F' ? 'Female' : ''}{patient.age ? `, ${patient.age}${patient.ageUnit === 'months' ? 'mo' : 'y'}` : ''}
            </Text>
          )}
        </HStack>
        <HStack spacing={3} mt={1}>
          {deviceCount > 0 && <Text fontSize="xs" color="micu.textSecondary">{deviceCount} device{deviceCount > 1 ? 's' : ''}</Text>}
          {medCount > 0 && <Text fontSize="xs" color="micu.textSecondary">{medCount} med{medCount > 1 ? 's' : ''}</Text>}
        </HStack>
      </Box>
      <HStack spacing={2}>
        <SyncBadge status={c.syncStatus} pendingCount={0} />
        <IconButton
          icon={<DeleteIcon />}
          size="sm"
          variant="micuGhost"
          aria-label="Delete case"
          color="micu.red"
          onClick={(e) => { e.stopPropagation(); onDelete(c); }}
        />
      </HStack>
    </Flex>
  );
};

const MICUCaseList = () => {
  const navigate = useNavigate();
  const { isDark } = useMICUTheme();
  const [cases, setCases] = useState([]);
  const [patients, setPatients] = useState({});
  const [deviceCounts, setDeviceCounts] = useState({});
  const [medCounts, setMedCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [deletingCase, setDeletingCase] = useState(null);
  const cancelRef = React.useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const loadData = useCallback(async () => {
    setLoading(true);
    const allCases = await db.cases.orderBy('createdAt').reverse().toArray();
    setCases(allCases);

    const pats = {};
    const devs = {};
    const meds = {};
    for (const c of allCases) {
      const p = await db.patients.where('caseId').equals(c.id).first();
      if (p) pats[c.id] = p;
      devs[c.id] = await db.devices.where('caseId').equals(c.id).count();
      meds[c.id] = await db.medications.where('caseId').equals(c.id).count();
    }
    setPatients(pats);
    setDeviceCounts(devs);
    setMedCounts(meds);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeleteConfirm = async () => {
    if (deletingCase) {
      await deleteCase(deletingCase.id);
      onClose();
      setDeletingCase(null);
      loadData();
    }
  };

  const handleDeleteClick = (c) => {
    setDeletingCase(c);
    onOpen();
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="300px">
        <Spinner color="micu.teal" size="lg" />
      </Flex>
    );
  }

  return (
    <Box p={4}>
      <Flex
        p={4}
        bg={isDark ? 'micu.bgTertiary' : 'micu.bgTertiary'}
        border="2px dashed"
        borderColor="micu.teal"
        borderRadius="lg"
        cursor="pointer"
        justify="center"
        align="center"
        _hover={{ bg: isDark ? 'micu.cardHover' : 'micu.bgSecondary' }}
        onClick={() => navigate('/micu-mobile/new')}
        mb={4}
      >
        <HStack spacing={2}>
          <AddIcon color="micu.teal" />
          <Text color="micu.teal" fontWeight="600">New Case</Text>
        </HStack>
      </Flex>

      <VStack spacing={3} align="stretch">
        {cases.length === 0 ? (
          <Text textAlign="center" color="micu.textMuted" py={8}>
            No cases yet. Tap "New Case" to start.
          </Text>
        ) : (
          cases.map(c => (
            <CaseRow
              key={c.id}
              c={c}
              patients={patients}
              deviceCounts={deviceCounts}
              medCounts={medCounts}
              onDelete={handleDeleteClick}
              navigate={navigate}
              isDark={isDark}
            />
          ))
        )}
      </VStack>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent bg={isDark ? 'micu.bgSecondary' : 'micu.bg'} color="micu.text">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Case</AlertDialogHeader>
            <AlertDialogBody>
              Delete "{deletingCase?.name}"? This removes all devices, medications, and logs for this case.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} variant="micuGhost">Cancel</Button>
              <Button variant="micuDanger" onClick={handleDeleteConfirm} ml={3}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default MICUCaseList;
